// LB-18b / epic D45 — "freeze and reload".
//
// A tab edits a listing at the version it hydrated from. The moment it
// learns the listing moved without it — a 409 version_conflict, a 428
// version_required, or a version it did not produce (a side route's
// header, a reload while edits were unsaved) — the LISTING is frozen for
// this tab:
// - nothing is sent for it (section savers, whole-form saves, side routes,
//   the page-exit keepalive, admin editors, destroy-flushes);
// - every control that writes is disabled, and one banner
//   (ListingFrozenBanner) lists the unsaved values so they can be copied;
// - its single action, Reload, re-fetches the listing for editing,
//   rehydrates the page once, clears the unsaved edits and unfreezes.
// There is no Keep mine / Use theirs and no per-section reconciliation.
import Vue from "vue";

export const FROZEN_MESSAGE =
  "This listing was changed in another tab or by a teammate. Reload to see the latest.";

// Reactive, so buttons bound to `isHomeFrozen(...)` disable themselves.
// `versionCheckOff`: the server's kill switch is on (it answers
// `X-LB-Version-Check: off`), so the client never freezes.
export const freezeState = Vue.observable({ frozen: {}, versionCheckOff: false });

const pendingProbes = new Set();
// LB-26 P1-1: the server's current values from each 409 version_conflict
// (`current`: the keys that request sent), shown as "Now saved".
const conflictCurrent = {};

// LB-26 P1-3: `body.listing-frozen` while any listing is frozen, so fixed
// bars (floating Next / Back, sticky Invest Now) can hide under the dialog.
export const FROZEN_BODY_CLASS = "listing-frozen";

function syncBodyClass() {
  if (typeof document === "undefined" || !document.body) return;
  const on = Object.keys(freezeState.frozen).length > 0;
  document.body.classList.toggle(FROZEN_BODY_CLASS, on);
}
const reloaders = new Set();
const unsavedSources = new Set();
const inputFlushers = new Set();

export class ListingFrozenError extends Error {
  constructor(homeId) {
    super(FROZEN_MESSAGE);
    this.name = "ListingFrozenError";
    this.code = "listing_frozen";
    this.frozen = true;
    this.homeId = homeId;
  }
}

export function isListingFrozenError(error) {
  return Boolean(error && (error.name === "ListingFrozenError" || error.frozen));
}

export function isHomeFrozen(homeId) {
  return homeId != null && Boolean(freezeState.frozen[String(homeId)]);
}

export function frozenHomeIds() {
  return Object.keys(freezeState.frozen);
}

export function frozenReason(homeId) {
  const entry = freezeState.frozen[String(homeId)];
  return entry ? entry.reason : null;
}

// The server's kill switch (update_home_version_check_disabled) bypasses
// the version check; while it is on, freezing is a no-op.
export function setVersionCheckOff(off) {
  freezeState.versionCheckOff = Boolean(off);
}

export function isVersionCheckOff() {
  return freezeState.versionCheckOff;
}

// Idempotent: the first reason is kept (one banner, never re-opened).
export function freezeHome(homeId, reason) {
  if (homeId == null || isHomeFrozen(homeId)) return;
  if (freezeState.versionCheckOff) return;
  Vue.set(freezeState.frozen, String(homeId), {
    reason: reason || "changed",
    at: new Date().toISOString(),
  });
  syncBodyClass();
}

export function unfreezeHome(homeId) {
  if (homeId == null) return;
  Vue.delete(freezeState.frozen, String(homeId));
  delete conflictCurrent[String(homeId)];
  syncBodyClass();
}

// LB-26 R2-6 (P2-5): listings the issuer builder loaded as archived
// (`archived` / `archived_at` on the payload). Their section savers send
// nothing (no autosave) and every step is read-only under a notice.
export const archivedState = Vue.observable({ homes: {} });

// `{ archivedAt, reason }` for an archived listing payload, else null.
export function archiveInfoOf(home) {
  if (!home || typeof home !== "object") return null;
  if (home.archived !== true && !home.archived_at) return null;
  return {
    archivedAt: home.archived_at || null,
    reason: home.archive_reason || null,
  };
}

export function markHomeArchived(homeId, info) {
  if (homeId == null) return;
  const key = String(homeId);
  if (info) Vue.set(archivedState.homes, key, { ...info });
  else if (archivedState.homes[key]) Vue.delete(archivedState.homes, key);
}

export function isHomeArchived(homeId) {
  return homeId != null && Boolean(archivedState.homes[String(homeId)]);
}

// A 409's `current` (merged: several sections may be refused in turn).
export function recordConflictCurrent(homeId, current) {
  if (homeId == null || !current || typeof current !== "object") return;
  const key = String(homeId);
  conflictCurrent[key] = { ...(conflictCurrent[key] || {}), ...current };
}

export function conflictCurrentFor(homeId) {
  return { ...(conflictCurrent[String(homeId)] || {}) };
}

// listing-save registers "does this listing have unsaved edits in this tab".
export function registerPendingProbe(probe) {
  pendingProbes.add(probe);
  return () => pendingProbes.delete(probe);
}

// Asks every source about all listings at once (the leave prompt).
export const ANY_LISTING = Symbol("any-listing");

export function homeHasUnsavedWork(homeId) {
  const probed = Array.from(pendingProbes).some((probe) => {
    try {
      return Boolean(probe(homeId));
    } catch (_) {
      return false;
    }
  });
  return probed || unsavedFromSources(homeId).length > 0;
}

// Any whole-form editor with unsaved values, for any listing (optionally
// ignoring one listing).
export function anyUnsavedFromSources(exceptHomeId) {
  return unsavedFromSources(ANY_LISTING).some(
    (entry) =>
      exceptHomeId == null ||
      entry.homeId == null ||
      String(entry.homeId) !== String(exceptHomeId)
  );
}

// Unsaved values that are not in a section saver (whole-form editors such
// as the admin Terms / Status / Metrics / Neighborhood forms): a source is
// `homeId => [{ section, text }]`. Shown by the banner, never sent.
export function registerUnsavedSource(source) {
  unsavedSources.add(source);
  return () => unsavedSources.delete(source);
}

// Pages whose inputs buffer edits (CKEditor) register `flushInputs()` so
// the banner captures what is on screen, not the last debounced copy.
export function registerInputFlusher(flusher) {
  inputFlushers.add(flusher);
  return () => inputFlushers.delete(flusher);
}

export function flushListingInputs(homeId) {
  inputFlushers.forEach((flusher) => {
    try {
      flusher(homeId);
    } catch (_) {
      // A page that cannot flush still shows what its savers hold.
    }
  });
}

export function unsavedFromSources(homeId) {
  const out = [];
  unsavedSources.forEach((source) => {
    try {
      (source(homeId) || []).forEach((entry) => {
        if (entry && entry.text) out.push(entry);
      });
    } catch (_) {
      // One broken source never hides the others.
    }
  });
  return out;
}

// A page that can rehydrate itself registers `reloader(homeId)`, which
// re-fetches (forEdit) and rehydrates, resolving true when it handled
// that listing.
export function registerListingReloader(reloader) {
  reloaders.add(reloader);
  return () => reloaders.delete(reloader);
}

// Runs the registered page reloaders once. Resolves to the number that
// handled `homeId` (0: nothing on screen could; the banner falls back to a
// full page reload).
export async function runListingReloaders(homeId) {
  const results = await Promise.all(
    Array.from(reloaders).map(async (reloader) => {
      try {
        return Boolean(await reloader(homeId));
      } catch (error) {
        // A failed reload keeps the listing frozen (rethrown below).
        return error;
      }
    })
  );
  const failure = results.find((r) => r instanceof Error);
  if (failure) throw failure;
  return results.filter((r) => r === true).length;
}

export function _resetFreezeForTests() {
  Object.keys(freezeState.frozen).forEach((key) => Vue.delete(freezeState.frozen, key));
  Object.keys(conflictCurrent).forEach((key) => delete conflictCurrent[key]);
  Object.keys(archivedState.homes).forEach((key) => Vue.delete(archivedState.homes, key));
  syncBodyClass();
  freezeState.versionCheckOff = false;
  reloaders.clear();
  unsavedSources.clear();
  inputFlushers.clear();
}
